import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  InputAdornment,
} from '@mui/material';
import { Search, Add, CheckCircle } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '../services/dashboardService';
import { WidgetDescriptor } from './types';
import { useAppTheme } from '../theme';

interface AddWidgetDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (descriptor: WidgetDescriptor) => void;
  /** Remove every placement of this key from the board. */
  onRemove: (widgetKey: string) => void;
  /** Keys already on the board — these render as selected. */
  existingKeys: string[];
}

/**
 * Widget picker.
 *
 * The catalogue comes from `GET /dashboards/registry`, which the server has
 * already filtered by role — the client never decides what a user may see. A
 * widget missing from this list is not hidden, it is not permitted.
 */
const AddWidgetDialog: React.FC<AddWidgetDialogProps> = ({
  open,
  onClose,
  onAdd,
  onRemove,
  existingKeys,
}) => {
  const { colors } = useAppTheme();
  const [search, setSearch] = React.useState('');
  const [category, setCategory] = React.useState<string>('all');

  const { data, isLoading } = useQuery<{ widgets: WidgetDescriptor[] }>({
    queryKey: ['widget-registry'],
    queryFn: () => dashboardService.registry(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  // Memoised: `?? []` yields a fresh array identity on every render, which
  // invalidates both useMemo hooks below every single time.
  const widgets: WidgetDescriptor[] = React.useMemo(() => data?.widgets ?? [], [data]);

  const categories = React.useMemo(() => {
    const set = new Set(widgets.map((w) => w.category));
    return ['all', ...Array.from(set).sort()];
  }, [widgets]);

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return widgets.filter((w) => {
      if (category !== 'all' && w.category !== category) return false;
      if (!term) return true;
      return (
        w.title.toLowerCase().includes(term) ||
        (w.description || '').toLowerCase().includes(term) ||
        w.category.toLowerCase().includes(term)
      );
    });
  }, [widgets, search, category]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Add a widget</DialogTitle>

      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search widgets…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
          {categories.map((c) => (
            <Chip
              key={c}
              label={c === 'all' ? 'All' : c}
              size="small"
              onClick={() => setCategory(c)}
              variant={category === c ? 'filled' : 'outlined'}
              color={category === c ? 'primary' : 'default'}
              sx={{ textTransform: 'capitalize' }}
            />
          ))}
        </Box>

        {isLoading && <Typography variant="body2" color="text.secondary">Loading widgets…</Typography>}

        {!isLoading && filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No widgets match that search.
          </Typography>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.25,
          }}
        >
          {filtered.map((w) => {
            const selected = existingKeys.includes(w.key);
            return (
              <Box
                key={w.key}
                role="checkbox"
                aria-checked={selected}
                tabIndex={0}
                // A tick mark reads as a toggle, so it has to behave as one.
                // It previously only ever added, which meant a second click
                // silently created a duplicate tile and there was no way to
                // take a widget off the board from here at all.
                onClick={() => (selected ? onRemove(w.key) : onAdd(w))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (selected) onRemove(w.key);
                    else onAdd(w);
                  }
                }}
                sx={{
                  p: 1.75,
                  border: '1px solid',
                  borderColor: selected ? colors.primary : colors.border,
                  backgroundColor: selected ? `${colors.primary}0f` : 'transparent',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'border-color .15s ease, background-color .15s ease',
                  '&:hover': {
                    borderColor: colors.primary,
                    backgroundColor: selected ? `${colors.primary}1a` : `${colors.primary}08`,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${colors.primary}`,
                    outlineOffset: 2,
                  },
                  // Reveal the removal affordance only on the selected state,
                  // so the default look stays calm.
                  '&:hover .widget-toggle-hint': { opacity: 1 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {w.title}
                  </Typography>
                  {selected ? (
                    <CheckCircle sx={{ fontSize: 17, color: colors.primary }} />
                  ) : (
                    <Add sx={{ fontSize: 17, color: 'text.disabled' }} />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {w.description || w.category}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                  <Typography variant="caption" color="text.disabled">
                    {w.defaultSize.w}×{w.defaultSize.h}
                  </Typography>
                  {selected && (
                    <Typography
                      className="widget-toggle-hint"
                      variant="caption"
                      sx={{ color: 'error.main', opacity: 0, transition: 'opacity .15s ease' }}
                    >
                      · click to remove
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddWidgetDialog;
